"use client";

import { useEffect, useState, useRef, useCallback } from "react";
import PostGrid from "@/components/post/PostGrid";
import type { Post } from "@/types/post";
import type { Category } from "@/types/category";
import { getPostsByCategoryAction } from "@/server/blog/posts.actions";

type Props = {
    initialPosts: Post[];
    currentCategoryId: number;
    categories: Category[];
    showCategory: boolean;
};

export default function PostInfiniteCategory({
                                            initialPosts,
                                            currentCategoryId,
                                            categories,
                                            // showCategory: initialShowCategory,
                                        }: Props) {

    // posts
    const [posts, setPosts] = useState<Post[]>(initialPosts);

    // pagination
    const [page, setPage] = useState(2);
    const [loading, setLoading] = useState(false);
    const [allLoaded, setAllLoaded] = useState(false);

    const fetchTriggerRef = useRef<HTMLDivElement | null>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const fetchPosts = useCallback(
        async (pageNumber: number) => {
            if (loading || allLoaded) return;

            try {
                setLoading(true);

                const newPosts = await getPostsByCategoryAction(currentCategoryId, pageNumber, 20);

                if (newPosts.length === 0) {
                    setAllLoaded(true);
                    return;
                }

                setPosts((prev) => {
                    const ids = new Set(prev.map((p) => p.id));
                    return [...prev, ...newPosts.filter((p) => !ids.has(p.id))];
                });
            } catch (err) {
                console.error(err);
                setAllLoaded(true);
            } finally {
                setLoading(false);
            }
        },
        [currentCategoryId, loading, allLoaded]
    );

    // infinite scroll (optimized)
    useEffect(() => {
        const el = fetchTriggerRef.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !loading && !allLoaded) {
                    fetchPosts(page);
                    setPage((p) => p + 1);
                }
            },
            {
                rootMargin: "800px", // سبک‌تر از 1200
                threshold: 0.01,
            }
        );

        observer.observe(el);
        observerRef.current = observer;

        return () => observer.disconnect();
    }, [fetchPosts, page, loading, allLoaded]);

    return (
        <>
            <PostGrid
                posts={posts}
                categories={categories}
                // showCategory={showCategory}
            />

            {!allLoaded && (
                <div className="py-10 flex justify-center">
                    {loading && (
                        <div className="w-7 h-7 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    )}
                    <div ref={fetchTriggerRef} />
                </div>
            )}

            {allLoaded && (
                <div className="text-center py-10 text-gray-400">
                    مقاله بیشتری وجود ندارد
                </div>
            )}
        </>
    );
}